import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import { PanelLeft } from 'lucide-react';
import {
  type ComponentProps,
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

const STORAGE_KEY = 'sidebar:state';
const KEYBOARD_SHORTCUT = 'b';

type SidebarState = 'expanded' | 'collapsed';

interface SidebarContextValue {
  state: SidebarState;
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

export function useSidebar(): SidebarContextValue {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}

export function SidebarProvider({ children }: { children: ReactNode }) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = useState(false);
  const [open, setOpenState] = useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }
    return window.localStorage.getItem(STORAGE_KEY) !== 'collapsed';
  });

  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
    window.localStorage.setItem(STORAGE_KEY, next ? 'expanded' : 'collapsed');
  }, []);

  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setOpenMobile((value) => !value);
      return;
    }
    setOpen(!open);
  }, [isMobile, open, setOpen]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        toggleSidebar();
      }
      if (event.key === 'Escape') {
        setOpenMobile(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggleSidebar]);

  const value = useMemo<SidebarContextValue>(
    () => ({
      state: open ? 'expanded' : 'collapsed',
      open,
      setOpen,
      openMobile,
      setOpenMobile,
      isMobile,
      toggleSidebar,
    }),
    [open, setOpen, openMobile, isMobile, toggleSidebar],
  );

  return (
    <SidebarContext value={value}>
      <div className="flex min-h-svh w-full bg-[var(--color-background)]">{children}</div>
    </SidebarContext>
  );
}

export function Sidebar({ children }: { children: ReactNode }) {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

  if (isMobile) {
    if (!openMobile) {
      return null;
    }
    return (
      <div className="fixed inset-0 z-50">
        <button
          type="button"
          aria-label="Close navigation"
          className="absolute inset-0 bg-black/50"
          onClick={() => setOpenMobile(false)}
        />
        <div className="group absolute inset-y-0 left-0 flex w-64 flex-col border-r border-[var(--color-sidebar-border)] bg-[var(--color-sidebar)] text-[var(--color-sidebar-foreground)]">
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      data-state={state}
      className={cn(
        'group sticky top-0 hidden h-svh shrink-0 flex-col border-r border-[var(--color-sidebar-border)] bg-[var(--color-sidebar)] text-[var(--color-sidebar-foreground)] transition-[width] duration-200 ease-linear md:flex',
        state === 'expanded' ? 'w-64' : 'w-14',
      )}
    >
      {children}
    </div>
  );
}

export function SidebarTrigger({ className, ...props }: ComponentProps<typeof Button>) {
  const { toggleSidebar } = useSidebar();
  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn('size-8 px-0', className)}
      aria-label="Toggle sidebar"
      onClick={toggleSidebar}
      {...props}
    >
      <PanelLeft className="size-4" />
    </Button>
  );
}

export function SidebarInset({ className, ...props }: ComponentProps<'main'>) {
  return <main className={cn('flex min-h-svh flex-1 flex-col', className)} {...props} />;
}

export function SidebarHeader({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('flex flex-col gap-2 p-2', className)} {...props} />;
}

export function SidebarFooter({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('mt-auto flex flex-col gap-2 p-2', className)} {...props} />;
}

export function SidebarContent({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex min-h-0 flex-1 flex-col gap-2 overflow-auto p-2', className)}
      {...props}
    />
  );
}

export function SidebarGroup({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('flex flex-col gap-1 py-2', className)} {...props} />;
}

export function SidebarGroupLabel({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'px-2 py-1 text-xs font-medium text-[var(--color-sidebar-foreground)]/60 group-data-[state=collapsed]:hidden',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarMenu({ className, ...props }: ComponentProps<'ul'>) {
  return <ul className={cn('flex w-full flex-col gap-1', className)} {...props} />;
}

export function SidebarMenuItem({ className, ...props }: ComponentProps<'li'>) {
  return <li className={cn('relative', className)} {...props} />;
}

const menuButtonVariants = cva(
  'flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none transition-colors group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:[&>span]:hidden hover:bg-[var(--color-sidebar-accent)] hover:text-[var(--color-sidebar-accent-foreground)] focus-visible:ring-2 focus-visible:ring-[var(--color-sidebar-ring)] disabled:pointer-events-none disabled:opacity-50 [&>svg]:size-4 [&>svg]:shrink-0',
  {
    variants: {
      isActive: {
        true: 'bg-[var(--color-sidebar-accent)] font-medium text-[var(--color-sidebar-accent-foreground)]',
        false: '',
      },
    },
    defaultVariants: {
      isActive: false,
    },
  },
);

export function SidebarMenuButton({
  className,
  asChild = false,
  isActive,
  tooltip,
  ...props
}: ComponentProps<'button'> &
  VariantProps<typeof menuButtonVariants> & { asChild?: boolean; tooltip?: string }) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp title={tooltip} className={cn(menuButtonVariants({ isActive }), className)} {...props} />
  );
}

export function SidebarMenuSub({ className, ...props }: ComponentProps<'ul'>) {
  return (
    <ul
      className={cn(
        'mx-3.5 flex flex-col gap-1 border-l border-[var(--color-sidebar-border)] py-1 pl-2.5 group-data-[state=collapsed]:hidden',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarMenuSubItem({ className, ...props }: ComponentProps<'li'>) {
  return <li className={cn('relative', className)} {...props} />;
}

export function SidebarMenuSubButton({
  className,
  asChild = false,
  isActive,
  ...props
}: ComponentProps<'a'> & { asChild?: boolean; isActive?: boolean }) {
  const Comp = asChild ? Slot : 'a';
  return (
    <Comp
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors hover:bg-[var(--color-sidebar-accent)] hover:text-[var(--color-sidebar-accent-foreground)] focus-visible:ring-2 focus-visible:ring-[var(--color-sidebar-ring)]',
        isActive &&
          'bg-[var(--color-sidebar-accent)] font-medium text-[var(--color-sidebar-accent-foreground)]',
        className,
      )}
      {...props}
    />
  );
}
